import { SlotWorkflowClient } from '@frontend/slot/api';
import { AvailableSlot, AvailableSlotConstraint, Slot } from '@frontend/slot/types';
import { Stock } from '@frontend/stock/types';

export interface FetchFirstAvailableSlotOptions {
    /**
     * Prefer a compatible slot that already contains stock. Used when an
     * authenticated user returns equipment that was lost / returned later.
     */
    preferSlotWithContent?: boolean;
    /**
     * Only return a compatible slot when it has no stock.
     */
    requireEmpty?: boolean;
    /**
     * Product that may not already be present in the selected slot.
     */
    excludedProductId?: string | null;
    /**
     * Products that may not already be present in the selected slot.
     */
    excludedProductIds?: string[] | null;
    /**
     * Optional per-product quantity limits. When an excluded product has a
     * limit, slots are only hidden once their current stock quantity for that
     * product reaches the limit.
     */
    excludedProductQuantityLimits?: Map<string, number> | Record<string, number> | null;
}

export interface AvailableSlotOption {
    slot: Slot;
    stocks: Stock[];
    hasContent: boolean;
}

export interface SlotOptionsByProduct {
    productId: string;
    options: AvailableSlotOption[];
}

export interface ChooseBestSingleSlotOptionOptions {
    addedProductQuantities: Map<string, number> | Record<string, number>;
    productQuantityLimits: Map<string, number> | Record<string, number>;
    allowedProductIds?: string[] | null;
    requireEmpty?: boolean;
    allowEmptyFallback?: boolean;
}

/**
 * Fetch the first available slot for the given constraints.
 *
 * This is a thin wrapper around `SlotWorkflowClient.postAvailableSlots` that
 * returns the first result (or `null` if none are available). The caller is
 * free to pass any `AvailableSlotConstraint` shape, so this helper is not tied
 * to a specific flow (return, drop-off, replenishment, ...).
 */
export const fetchFirstAvailableSlot = async (
    accountId: string,
    spotId: string,
    constraints: AvailableSlotConstraint,
    options: FetchFirstAvailableSlotOptions = {}
): Promise<Slot | null> => {
    const excludedProductIds = normalizeExcludedProductIds(options);
    const excludedProductQuantityLimits = normalizeExcludedProductQuantityLimits(options.excludedProductQuantityLimits);
    if (!options.preferSlotWithContent && !options.requireEmpty && excludedProductIds.size === 0) {
        const result = await SlotWorkflowClient.postAvailableSlots(accountId, spotId, constraints);
        return result.results[0] ?? null;
    }

    const result = await SlotWorkflowClient.postAvailableSlots(accountId, spotId, constraints);
    let firstCompatibleSlot: Slot | null = null;

    for (const slot of result.results) {
        const stocks = getAvailableSlotStocks(slot);
        if (containsBlockedProduct(stocks, excludedProductIds, excludedProductQuantityLimits)) continue;

        const hasContent = stocks.length > 0;
        if (options.requireEmpty) {
            if (!hasContent) return slot;
            continue;
        }

        firstCompatibleSlot ??= slot;
        if (!options.preferSlotWithContent || hasContent) {
            return slot;
        }
    }

    return firstCompatibleSlot;
};

export const fetchAvailableSlotOptions = async (
    accountId: string,
    spotId: string,
    constraints: AvailableSlotConstraint,
    options: Pick<FetchFirstAvailableSlotOptions, 'excludedProductId' | 'excludedProductIds' | 'excludedProductQuantityLimits' | 'requireEmpty'> & {
        sortByContent?: boolean;
    } = {}
): Promise<AvailableSlotOption[]> => {
    const excludedProductIds = normalizeExcludedProductIds(options);
    const excludedProductQuantityLimits = normalizeExcludedProductQuantityLimits(options.excludedProductQuantityLimits);
    const result = await SlotWorkflowClient.postAvailableSlots(accountId, spotId, constraints);
    const slotsWithStocks = result.results.map((slot) => {
        const stocks = getAvailableSlotStocks(slot);
        return { slot, stocks, hasContent: stocks.length > 0 };
    });

    const filteredSlots = slotsWithStocks.filter(
        ({ stocks }) => !containsBlockedProduct(stocks, excludedProductIds, excludedProductQuantityLimits) && (!options.requireEmpty || stocks.length === 0)
    );
    if (!options.sortByContent) return filteredSlots;

    return filteredSlots.sort((a, b) => Number(b.hasContent) - Number(a.hasContent));
};

export const chooseBestSingleSlotOption = (groups: SlotOptionsByProduct[], options: ChooseBestSingleSlotOptionOptions): AvailableSlotOption | null => {
    if (groups.length === 0) return null;

    const addedProductQuantities = normalizeProductQuantityMap(options.addedProductQuantities);
    const productQuantityLimits = normalizeProductQuantityMap(options.productQuantityLimits);
    const allowedProductIds = options.allowedProductIds ?? [...productQuantityLimits.keys()];
    const allowedProductIdSet = new Set(allowedProductIds);
    const commonSlotIds = groups.reduce<Set<string> | null>((acc, group) => {
        const groupSlotIds = new Set(group.options.map((option) => option.slot.id));
        if (!acc) return groupSlotIds;
        return new Set([...acc].filter((slotId) => groupSlotIds.has(slotId)));
    }, null);

    if (!commonSlotIds || commonSlotIds.size === 0) return null;

    const optionsBySlotId = new Map<string, AvailableSlotOption>();
    for (const group of groups) {
        for (const option of group.options) {
            if (!optionsBySlotId.has(option.slot.id)) optionsBySlotId.set(option.slot.id, option);
        }
    }

    const candidates = [...commonSlotIds]
        .map((slotId) => optionsBySlotId.get(slotId))
        .filter((option): option is AvailableSlotOption => !!option)
        .filter((option) =>
            doesSingleSlotFitProducts(option, {
                addedProductQuantities,
                productQuantityLimits,
                allowedProductIds: allowedProductIdSet,
                requireEmpty: options.requireEmpty
            })
        );

    if (candidates.length === 0) return null;

    return candidates.sort((a, b) => {
        if (options.allowEmptyFallback && !options.requireEmpty) {
            const contentDiff = Number(a.stocks.length === 0) - Number(b.stocks.length === 0);
            if (contentDiff !== 0) return contentDiff;
        }

        const remainingDiff =
            remainingProductQuantity(a, addedProductQuantities, productQuantityLimits, allowedProductIds) -
            remainingProductQuantity(b, addedProductQuantities, productQuantityLimits, allowedProductIds);
        if (remainingDiff !== 0) return remainingDiff;

        return (a.slot.display || a.slot.number || a.slot.id).localeCompare(b.slot.display || b.slot.number || b.slot.id);
    })[0];
};

const getAvailableSlotStocks = (slot: AvailableSlot): Stock[] => slot.stocks ?? [];

const normalizeExcludedProductIds = (options: Pick<FetchFirstAvailableSlotOptions, 'excludedProductId' | 'excludedProductIds'>): Set<string> =>
    new Set([...(options.excludedProductIds ?? []), ...(options.excludedProductId ? [options.excludedProductId] : [])]);

const normalizeExcludedProductQuantityLimits = (limits: FetchFirstAvailableSlotOptions['excludedProductQuantityLimits']): Map<string, number> => {
    if (!limits) return new Map();
    if (limits instanceof Map) return limits;
    return new Map(Object.entries(limits));
};

const normalizeProductQuantityMap = (quantities: Map<string, number> | Record<string, number>): Map<string, number> =>
    quantities instanceof Map ? quantities : new Map(Object.entries(quantities));

const stockQuantityForProduct = (stocks: Stock[], productId: string): number =>
    stocks.reduce((total, stock) => total + (stock.product_id === productId ? stock.quantity || 1 : 0), 0);

const doesSingleSlotFitProducts = (
    option: AvailableSlotOption,
    {
        addedProductQuantities,
        productQuantityLimits,
        allowedProductIds,
        requireEmpty
    }: {
        addedProductQuantities: Map<string, number>;
        productQuantityLimits: Map<string, number>;
        allowedProductIds: Set<string>;
        requireEmpty?: boolean;
    }
): boolean => {
    if (requireEmpty && option.stocks.length > 0) return false;
    if (option.stocks.some((stock) => !allowedProductIds.has(stock.product_id))) return false;

    for (const productId of allowedProductIds) {
        const limit = productQuantityLimits.get(productId);
        if (limit === undefined) return false;
        const addedQuantity = addedProductQuantities.get(productId) ?? 0;
        if (stockQuantityForProduct(option.stocks, productId) + addedQuantity > limit) return false;
    }

    return true;
};

const remainingProductQuantity = (
    option: AvailableSlotOption,
    addedProductQuantities: Map<string, number>,
    productQuantityLimits: Map<string, number>,
    productIds: string[]
): number =>
    productIds.reduce((remaining, productId) => {
        const limit = productQuantityLimits.get(productId) ?? 0;
        const currentQuantity = stockQuantityForProduct(option.stocks, productId) + (addedProductQuantities.get(productId) ?? 0);
        return remaining + Math.max(limit - currentQuantity, 0);
    }, 0);

const containsBlockedProduct = (stocks: Stock[], productIds: Set<string>, quantityLimits: Map<string, number>): boolean => {
    if (productIds.size === 0) return false;

    for (const productId of productIds) {
        const quantity = stockQuantityForProduct(stocks, productId);
        if (quantity === 0) continue;

        const limit = quantityLimits.get(productId);
        if (limit === undefined || quantity >= limit) return true;
    }

    return false;
};
